interface Props {
  name: string;
  description: string | null;
  frontmatter: Record<string, unknown>;
  hasSoulMd: boolean;
  onAddSoulMd?: () => void;
}

const HERO_KEYS = new Set(['name', 'description']);

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function AgentFrontmatter({ name, description, frontmatter, hasSoulMd, onAddSoulMd }: Props) {
  const otherEntries = Object.entries(frontmatter).filter(([k]) => !HERO_KEYS.has(k));

  return (
    <div
      style={{
        padding: '20px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div
        style={{
          fontFamily: HEADING_SERIF,
          fontSize: 26,
          fontWeight: 500,
          color: 'var(--text)',
          letterSpacing: '-0.018em',
          lineHeight: 1.15,
        }}
      >
        {name}
      </div>
      {description && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: 'var(--text-soft)',
            lineHeight: 1.55,
            maxWidth: 620,
          }}
        >
          {description}
        </div>
      )}

      {otherEntries.length > 0 && (
        <dl
          style={{
            marginTop: 18,
            marginBottom: 0,
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 20,
            rowGap: 8,
          }}
        >
          {otherEntries.map(([k, v]) => {
            const rendered = stringifyValue(v);
            const multiline = rendered.includes('\n');
            return <Row key={k} k={k} v={rendered} multiline={multiline} />;
          })}
        </dl>
      )}

      {!hasSoulMd && <SoulCallout onAdd={onAddSoulMd} />}
    </div>
  );
}

function Row({ k, v, multiline }: { k: string; v: string; multiline: boolean }) {
  return (
    <>
      <dt
        style={{
          fontFamily: MONO,
          fontSize: 10.5,
          color: 'var(--text-soft)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          paddingTop: multiline ? 2 : 1,
        }}
      >
        {k}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: multiline ? 11.5 : 12.5,
          color: 'var(--text)',
          fontFamily: multiline ? MONO : 'inherit',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.5,
        }}
      >
        {v}
      </dd>
    </>
  );
}

function SoulCallout({ onAdd }: { onAdd?: () => void }) {
  return (
    <div
      style={{
        marginTop: 22,
        padding: '14px 16px',
        background: 'var(--bg-alt)',
        border: '1px dashed var(--border-strong)',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: '50%',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: HEADING_SERIF,
          fontSize: 14,
          fontStyle: 'italic',
          color: 'var(--text-soft)',
        }}
      >
        s
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>
          No SOUL.md yet
        </div>
        <div style={{ marginTop: 2, fontSize: 11.5, color: 'var(--text-soft)', lineHeight: 1.45 }}>
          Give this agent a working personality &mdash; tone, defaults, points of view.
        </div>
      </div>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          style={{
            padding: '5px 11px',
            fontSize: 11.5,
            fontFamily: 'inherit',
            background: 'transparent',
            color: 'var(--accent)',
            border: '1px solid var(--accent)',
            borderRadius: 5,
            cursor: 'pointer',
            fontWeight: 500,
            letterSpacing: '0.01em',
            flexShrink: 0,
          }}
        >
          Add SOUL.md
        </button>
      )}
    </div>
  );
}

const HEADING_SERIF =
  "ui-serif, Georgia, 'Iowan Old Style', 'Apple Garamond', Palatino, serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
